// Reads the signed-in person's own Outlook calendar and sent mail from
// Microsoft Graph, for suggested time entries. Only subjects, people and
// times are read (Mail.ReadBasic never includes message bodies), and only for
// the week shown.

// DEPENDENCIES
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import axios from "axios";

// DATA / HELPERS
import { isoOf } from "./timeEntryUtils";

const GRAPH = "https://graph.microsoft.com/v1.0";
export const GRAPH_SCOPES = ["Calendars.Read", "Mail.ReadBasic"];

// Events longer than this (conferences, multi-day blocks) aren't suggested
const MAX_EVENT_HOURS = 12;
const MAX_PAGES = 10;

// Thrown when Microsoft needs the person to sign in again before calendar /
// mail can be read (an expired session; access itself is admin-consented)
export class OutlookSignInNeeded extends Error {}

/**
 * A Graph token for calendar and mail
 * @param {boolean} interactive - true: show Microsoft's sign-in popup if needed
 */
export async function getGraphToken(instance, account, interactive = false) {
	try {
		const res = await instance.acquireTokenSilent({ scopes: GRAPH_SCOPES, account });
		return res.accessToken;
	} catch (error) {
		if (!(error instanceof InteractionRequiredAuthError)) throw error;
		if (!interactive) throw new OutlookSignInNeeded("Microsoft sign-in needed for calendar and email");
		const res = await instance.acquireTokenPopup({ scopes: GRAPH_SCOPES, account });
		return res.accessToken;
	}
}

// GET a Graph list, following @odata.nextLink
const graphGetAll = async (url, token, headers = {}) => {
	const rows = [];
	let next = url;
	for (let page = 0; next && page < MAX_PAGES; page++) {
		const res = await axios.get(next, { headers: { Authorization: `Bearer ${token}`, ...headers } });
		rows.push(...(res.data?.value || []));
		next = res.data?.["@odata.nextLink"] || null;
	}
	return rows;
};

const utcMs = (graphDateTime) =>
	graphDateTime?.dateTime ? Date.parse(graphDateTime.dateTime.replace(/Z?$/, "Z")) : NaN;

const person = (r) => ({
	email: String(r?.emailAddress?.address || "").toLowerCase(),
	name: r?.emailAddress?.name || "",
});

const MEETING_RESPONSE_RE =
	/^\s*(accepted|declined|tentative|tentatively accepted|new time proposed|canceled|cancelled)\s*:/i;
// Graph marks them as eventMessage types; the subject check covers the rest
const isMeetingResponse = (m) =>
	String(m["@odata.type"] || "").includes("eventMessage") ||
	MEETING_RESPONSE_RE.test(m.subject || "");

// "RE: FW: Oakridge submittal" -> "Oakridge submittal"
export const cleanSubject = (subject) =>
	String(subject || "").replace(/^\s*((re|fw|fwd)\s*:\s*)+/i, "").trim();

/**
 * The week's timed calendar events (not declined or cancelled) and sent-email
 * conversations, one per conversation per day, in the shapes the server
 * matches to projects
 * @param {string} token - from getGraphToken
 * @param {Date} from - start of the week (local midnight)
 * @param {Date} to - end of the week (exclusive)
 * @param {string} selfEmail - the signed-in person, left out of "people"
 */
export async function readOutlookWeek(token, from, to, selfEmail) {
	const self = String(selfEmail || "").toLowerCase();
	const others = (people) => people.filter((p) => p.email && p.email !== self);
	const range = `startDateTime=${from.toISOString()}&endDateTime=${to.toISOString()}`;
	const [rawEvents, rawMail] = await Promise.all([
		graphGetAll(
			`${GRAPH}/me/calendarView?${range}&$top=100` +
				"&$select=id,subject,start,end,isAllDay,isCancelled,responseStatus,attendees,organizer,location",
			token,
			{ Prefer: 'outlook.timezone="UTC"' },
		),
		graphGetAll(
			`${GRAPH}/me/mailFolders/SentItems/messages?$top=100` +
				`&$filter=sentDateTime ge ${from.toISOString()} and sentDateTime lt ${to.toISOString()}` +
				"&$orderby=sentDateTime&$select=id,subject,sentDateTime,toRecipients,ccRecipients,conversationId",
			token,
		),
	]);

	const events = rawEvents
		.filter((e) => !e.isAllDay && !e.isCancelled && e.responseStatus?.response !== "declined")
		.map((e) => ({ e, start: utcMs(e.start), end: utcMs(e.end) }))
		.filter(({ start, end }) => end > start && end - start <= MAX_EVENT_HOURS * 3600000)
		.map(({ e, start, end }) => ({
			id: e.id,
			date: isoOf(new Date(start)),
			subject: e.subject || "",
			location: e.location?.displayName || "",
			start,
			end,
			people: others([...(e.attendees || []).map(person), person(e.organizer)]).filter(
				(p, i, all) => all.findIndex((x) => x.email === p.email) === i,
			),
		}));

	// Sent mail grouped per conversation per day. Meeting responses
	// ("Accepted: …") are left out: Outlook sends those, not the person.
	const threads = new Map();
	rawMail.filter((m) => !isMeetingResponse(m)).forEach((m) => {
		const at = Date.parse(m.sentDateTime);
		const date = isoOf(new Date(at));
		const key = (m.conversationId || m.id) + "|" + date;
		const t = threads.get(key) || {
			conversationId: m.conversationId || m.id,
			date,
			subject: cleanSubject(m.subject),
			count: 0,
			first: at,
			last: at,
			people: [],
		};
		t.count += 1;
		t.first = Math.min(t.first, at);
		t.last = Math.max(t.last, at);
		others([...(m.toRecipients || []), ...(m.ccRecipients || [])].map(person)).forEach((p) => {
			if (!t.people.some((x) => x.email === p.email)) t.people.push(p);
		});
		threads.set(key, t);
	});

	return { events, threads: [...threads.values()] };
}
